/**
 * Asset registry.
 *
 * Each entry is a sprite slot the game can use. We attempt to load every
 * declared image at preload time. If a file is missing (404), AssetManager
 * silently records that the slot is unavailable, and rendering falls back to
 * the graphics-primitive placeholder we've been using throughout development.
 *
 * To swap art in, drop files at the listed paths under `public/assets/`.
 * Phaser-served paths are relative to the page, so `assets/chimp.png` resolves
 * to `public/assets/chimp.png` after the Vite build.
 *
 * Recommended source resolution: 64x64 (matches TILE_SIZE). Phaser's
 * `pixelArt: true` will keep them crisp at any integer scale.
 *
 * Animated slots (animated: true) are loaded as spritesheets with TILE_SIZE
 * frames laid out horizontally. A 64×64 PNG works as 1 frame (static),
 * a 128×64 PNG works as 2 frames (walk animation). AssetManager autodetects
 * which by inspecting frame count after load.
 */

export interface AssetSpec {
  /** Phaser texture key (used by sprites that consume this asset). */
  key: string;
  /** URL relative to the served root. */
  url: string;
  /** If true, loaded as a multi-frame-capable spritesheet (frames laid horizontally). */
  animated?: boolean;
  /**
   * For animated assets, ms per frame of the registered loop animation.
   * Defaults to WALK_FRAME_DURATION_MS in AssetManager when omitted (good for
   * character walks). Water uses a slower value for a gentle ripple.
   */
  animFrameMs?: number;
  /** Recommended source resolution, for documentation only. */
  recommendedSize: string;
}

/** Sprite slots. Drop matching PNG files into `public/assets/`. */
export const SPRITE_ASSETS: AssetSpec[] = [
  { key: 'chimp',       url: 'assets/chimp.png',       animated: true,  recommendedSize: '64x64 (or 128x64 for 2-frame walk)' },
  { key: 'coconut',     url: 'assets/coconut.png',                       recommendedSize: '64x64' },
  { key: 'spider',      url: 'assets/spider.png',      animated: true,  recommendedSize: '64x64 (or 128x64 for 2-frame walk)' },
  { key: 'water',       url: 'assets/water.png',       animated: true,  animFrameMs: 320, recommendedSize: '64x64 static, or N×64 for an N-frame loop (e.g. 256x64 = 4 frames)' },
  { key: 'foliage',     url: 'assets/foliage.png',                       recommendedSize: '64x64' },
  { key: 'ground',      url: 'assets/ground.png',                        recommendedSize: '64x64' },
  { key: 'ground_alt',  url: 'assets/ground_alt.png',                    recommendedSize: '64x64 (optional checker variant)' },
];
